import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAgentMemory } from '@/hooks/useAgentMemory';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ShieldAlert, UploadCloud, Check, Ban, BrainCircuit, AlertTriangle, FileCheck, Loader2, Eye, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { imageUploadSchema } from '@/lib/validation';
import { useRateLimit } from '@/hooks/useRateLimit';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { SEO } from '@/components/SEO';
import { UpgradePrompt } from '@/components/UpgradePrompt';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useUsageTracking } from '@/hooks/useUsageTracking';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Invoice {
  id: string;
  vendor: string;
  invoice_no: string;
  date: string;
  amount: number;
  file_name: string | null;
  status: string;
  flag: string | null;
  risk_score: number | null;
  created_at: string | null;
}

export default function Watch() {
  const { user } = useAuth();
  const { logInteraction, updateOutcome } = useAgentMemory();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviewInvoice, setReviewInvoice] = useState<Invoice | null>(null);
  const [deleteInvoice, setDeleteInvoice] = useState<Invoice | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [stats, setStats] = useState({ total: 0, approved: 0, blocked: 0, pending: 0, highRisk: 0 });
  const { toast } = useToast();
  const { checkLimit } = useRateLimit({ maxCalls: 10, windowMs: 60000 });
  const { limits, isWithinLimit: checkPlanLimit } = usePlanLimits();
  const { usage, incrementInvoiceScans } = useUsageTracking();

  useEffect(() => {
    if (!user) return;
    fetchInvoices();
  }, [user]);

  const fetchInvoices = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('invoices')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      const invoiceData = data || [];
      setInvoices(invoiceData);
      
      setStats({
        total: invoiceData.length,
        approved: invoiceData.filter(i => i.status === 'approved').length,
        blocked: invoiceData.filter(i => i.status === 'blocked').length,
        pending: invoiceData.filter(i => i.status === 'pending').length,
        highRisk: invoiceData.filter(i => (i.risk_score || 0) > 50).length
      });
    } finally {
      setLoading(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0] || !user) return;

    if (!checkLimit()) {
      toast({
        variant: 'destructive',
        title: 'Rate Limit',
        description: 'Please wait before uploading more'
      });
      return;
    }

    // Check plan limit for invoice scans
    if (!checkPlanLimit('invoiceScans', usage.invoiceScans)) {
      toast({
        variant: 'destructive',
        title: 'Limit Reached',
        description: `You've reached your monthly limit of ${limits.invoiceScans} invoice scans. Please upgrade your plan.`
      });
      return;
    }

    setUploading(true);
    try {
      const file = e.target.files[0];
      imageUploadSchema.parse({ file });
      const base64 = await fileToBase64(file);

      await supabase.from('logs').insert({
        user_id: user.id,
        msg: 'Analyzing invoice with AI...',
        level: 'processing',
        time: new Date().toLocaleTimeString()
      });

      const { data: aiData, error } = await supabase.functions.invoke('analyze-invoice', {
        body: { imageData: base64 }
      });

      if (error) throw error;

      // Check for duplicates
      const exactDup = invoices.find(i => 
        i.invoice_no === aiData.invoiceNo && i.vendor === aiData.vendor
      );

      let riskScore = 0;
      let flagReason = null;

      if (exactDup) {
        riskScore = 100;
        flagReason = "Exact Invoice Number Duplicate";
      } else {
        const fuzzyDup = invoices.find(i => 
          i.amount === aiData.amount && 
          calculateSimilarity(i.vendor, aiData.vendor) > 0.8
        );
        if (fuzzyDup) {
          riskScore = 75;
          flagReason = "Similar Vendor & Amount";
        }
      }

      await supabase.from('invoices').insert({
        user_id: user.id,
        vendor: aiData.vendor,
        invoice_no: aiData.invoiceNo,
        date: aiData.date,
        amount: aiData.amount,
        file_name: file.name,
        status: riskScore > 50 ? 'pending' : 'approved',
        flag: flagReason,
        risk_score: riskScore
      });

      await supabase.from('logs').insert({
        user_id: user.id,
        msg: `Invoice parsed: ${aiData.vendor}`,
        level: 'success',
        time: new Date().toLocaleTimeString()
      });

      if (riskScore > 50) {
        await supabase.functions.invoke('send-notification', {
          body: {
            userId: user.id,
            title: 'High-Risk Invoice',
            message: `${aiData.vendor} - Risk: ${riskScore}%`,
            type: 'warning'
          }
        });
      }

      // Increment usage tracking
      await incrementInvoiceScans();

      toast({ title: "Invoice analyzed", description: riskScore > 50 ? `Risk: ${riskScore}% - Needs review` : 'Approved automatically' });
      fetchInvoices();
    } catch (error: any) {
      toast({ variant: "destructive", title: "Analysis failed", description: error.message });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const calculateSimilarity = (s1: string, s2: string) => {
    if (!s1 || !s2) return 0;
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    if (longer.length === 0) return 1.0;
    const s1Lower = longer.toLowerCase();
    const s2Lower = shorter.toLowerCase();
    let matchCount = 0;
    for (let i = 0; i < s2Lower.length; i++) {
      if (s1Lower.includes(s2Lower[i])) matchCount++;
    }
    return matchCount / longer.length;
  };

  const resolveDuplicate = async (invoiceId: string, action: 'approve' | 'block') => {
    if (!user) return;

    const invoice = invoices.find(i => i.id === invoiceId);
    
    // Log the interaction for agent learning
    const interactionId = await logInteraction({
      interaction_type: 'action',
      input: { invoiceId, action, vendor: invoice?.vendor, amount: invoice?.amount },
      context: { page: 'watch', riskScore: invoice?.risk_score },
      outcome: 'pending'
    });

    const status = action === 'block' ? 'blocked' : 'approved';
    const flag = action === 'block' ? 'Confirmed Duplicate' : null;

    await supabase.from('invoices').update({ status, flag }).eq('id', invoiceId);
    await supabase.from('logs').insert({
      user_id: user.id,
      msg: `Invoice ${status}`,
      level: action === 'block' ? 'warning' : 'success',
      time: new Date().toLocaleTimeString()
    });

    // Update outcome for learning
    if (interactionId) {
      await updateOutcome(interactionId, 'success', action === 'approve' ? 'positive' : 'negative');
    }

    toast({ title: `Invoice ${status}` });
    setReviewInvoice(null);
    fetchInvoices();
  };

  const handleDelete = async () => {
    if (!deleteInvoice || !user) return;
    
    await supabase.from('invoices').delete().eq('id', deleteInvoice.id);
    await supabase.from('logs').insert({
      user_id: user.id,
      msg: `Invoice deleted: ${deleteInvoice.vendor}`,
      level: 'warning',
      time: new Date().toLocaleTimeString()
    });
    
    toast({ title: "Invoice deleted" });
    setDeleteInvoice(null);
    fetchInvoices();
  };

  const handleBulkDelete = async () => {
    if (!user || selectedIds.length === 0) return;
    
    await supabase.from('invoices').delete().in('id', selectedIds);
    await supabase.from('logs').insert({
      user_id: user.id,
      msg: `Bulk deleted ${selectedIds.length} invoices`,
      level: 'warning',
      time: new Date().toLocaleTimeString()
    });
    
    toast({ title: `Deleted ${selectedIds.length} invoices` });
    setSelectedIds([]);
    setBulkDeleteOpen(false);
    fetchInvoices();
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === invoices.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(invoices.map(i => i.id));
    }
  };

  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  const getStatusBadge = (status: string, riskScore: number | null) => {
    switch (status) {
      case 'blocked':
        return <Badge variant="destructive" className="text-xs"><Ban className="h-3 w-3 mr-1" />Blocked</Badge>;
      case 'approved':
        return <Badge className="bg-success/10 text-success border-success/20 text-xs"><Check className="h-3 w-3 mr-1" />Approved</Badge>;
      default:
        return <Badge variant="secondary" className="bg-warning/10 text-warning border-warning/20 text-xs"><AlertTriangle className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <SEO title="WATCH Engine - Sentinel Pro" description="AI-powered invoice fraud detection" />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <ShieldAlert className="text-destructive shrink-0" size={28} />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground pb-1">WATCH Engine</h1>
            <p className="text-sm text-muted-foreground">AI-powered fraud detection</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card className="p-3 border-border/50">
          <div className="text-xl font-bold text-foreground">{stats.total}</div>
          <div className="text-xs text-muted-foreground">Total</div>
        </Card>
        <Card className="p-3 border-border/50">
          <div className="text-xl font-bold text-success">{stats.approved}</div>
          <div className="text-xs text-muted-foreground">Approved</div>
        </Card>
        <Card className="p-3 border-border/50">
          <div className="text-xl font-bold text-destructive">{stats.blocked}</div>
          <div className="text-xs text-muted-foreground">Blocked</div>
        </Card>
        <Card className="p-3 border-border/50">
          <div className="text-xl font-bold text-warning">{stats.pending}</div>
          <div className="text-xs text-muted-foreground">Pending</div>
        </Card>
        <Card className="p-3 border-border/50 col-span-2 sm:col-span-1">
          <div className="text-xl font-bold text-destructive">{stats.highRisk}</div>
          <div className="text-xs text-muted-foreground">High Risk</div>
        </Card>
      </div>

      {/* Usage Warning */}
      <UpgradePrompt type="invoiceScans" />

      {/* Upload Card */}
      <Card className="border-2 border-dashed border-border">
        <CardContent className="p-6">
          {uploading ? (
            <div className="py-6 text-center">
              <BrainCircuit className="animate-pulse mx-auto text-primary mb-4" size={40} />
              <h3 className="text-foreground font-bold mb-1">AI Analysis...</h3>
              <p className="text-sm text-muted-foreground">Extracting data & calculating risk</p>
            </div>
          ) : (
            <label className="block cursor-pointer">
              <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleUpload} />
              <div className="py-6 text-center">
                <UploadCloud size={40} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-bold text-foreground mb-1">Upload Invoice</h3>
                <p className="text-sm text-muted-foreground mb-3">Click or drag to upload image/PDF</p>
                <Button size="sm">Select File</Button>
              </div>
            </label>
          )}
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {/* Empty State */}
      {!loading && invoices.length === 0 && (
        <Card className="p-8 text-center border-border/50">
          <FileCheck className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Invoices Yet</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
            Upload your first invoice to start detecting duplicates and fraud with AI-powered analysis.
          </p>
          <label>
            <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleUpload} />
            <Button className="cursor-pointer">
              <UploadCloud className="mr-2 h-4 w-4" />
              Upload First Invoice
            </Button>
          </label>
        </Card>
      )}

      {/* Invoices List - Card layout for mobile */}
      {!loading && invoices.length > 0 && (
        <div className="space-y-3">
          {/* Bulk Actions Bar */}
          <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/50 rounded-lg border border-border">
            <Checkbox
              checked={selectedIds.length === invoices.length && invoices.length > 0}
              onCheckedChange={toggleSelectAll}
              aria-label="Select all"
            />
            <span className="text-sm text-muted-foreground">
              {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select all'}
            </span>
            {selectedIds.length > 0 && (
              <div className="flex gap-2 ml-auto">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Delete</span>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedIds([])}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block">
            <Card className="border-border/50 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface border-b border-border">
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <th className="p-4 w-10"></th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Vendor</th>
                      <th className="p-4">Invoice #</th>
                      <th className="p-4">Amount</th>
                      <th className="p-4">Risk</th>
                      <th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {invoices.map((inv) => (
                      <tr key={inv.id} className={`hover:bg-surface/50 transition-colors ${selectedIds.includes(inv.id) ? 'bg-primary/5' : ''}`}>
                        <td className="p-4">
                          <Checkbox
                            checked={selectedIds.includes(inv.id)}
                            onCheckedChange={() => toggleSelect(inv.id)}
                          />
                        </td>
                        <td className="p-4">{getStatusBadge(inv.status, inv.risk_score)}</td>
                        <td className="p-4 text-foreground font-medium max-w-[200px] truncate">{inv.vendor}</td>
                        <td className="p-4 text-muted-foreground text-sm">{inv.invoice_no}</td>
                        <td className="p-4 text-foreground">{formatCurrency(inv.amount)}</td>
                        <td className="p-4">
                          {(inv.risk_score || 0) > 50 ? (
                            <span className="text-destructive font-bold">{inv.risk_score}%</span>
                          ) : (
                            <span className="text-success">{inv.risk_score || 0}%</span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex justify-end gap-2">
                            {inv.status === 'pending' && (
                              <Button variant="outline" size="sm" onClick={() => setReviewInvoice(inv)}>
                                <Eye className="h-4 w-4 mr-1" />
                                Review
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => setDeleteInvoice(inv)}>
                              <Trash2 className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {invoices.map((inv) => (
              <Card key={inv.id} className={`p-4 border-border/50 ${selectedIds.includes(inv.id) ? 'ring-2 ring-primary' : ''}`}>
                <div className="flex items-start gap-3 mb-3">
                  <Checkbox
                    checked={selectedIds.includes(inv.id)}
                    onCheckedChange={() => toggleSelect(inv.id)}
                    className="mt-1"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-foreground truncate">{inv.vendor}</div>
                      {getStatusBadge(inv.status, inv.risk_score)}
                    </div>
                    <div className="text-xs text-muted-foreground">#{inv.invoice_no}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between pl-7">
                  <div>
                    <div className="text-lg font-bold text-foreground">{formatCurrency(inv.amount)}</div>
                    <div className="text-xs text-muted-foreground">
                      Risk: <span className={(inv.risk_score || 0) > 50 ? 'text-destructive font-bold' : 'text-success'}>{inv.risk_score || 0}%</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {inv.status === 'pending' && (
                      <Button variant="outline" size="sm" onClick={() => setReviewInvoice(inv)}>
                        Review
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => setDeleteInvoice(inv)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {inv.flag && (
                  <div className="mt-2 ml-7 text-xs text-warning bg-warning/10 px-2 py-1 rounded">
                    {inv.flag}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!reviewInvoice} onOpenChange={() => setReviewInvoice(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Review Invoice</DialogTitle>
            <DialogDescription>Approve or block this flagged invoice</DialogDescription>
          </DialogHeader>
          {reviewInvoice && (
            <div className="space-y-4">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Vendor</span>
                  <span className="font-medium text-foreground">{reviewInvoice.vendor}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Invoice #</span>
                  <span className="font-medium text-foreground">{reviewInvoice.invoice_no}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium text-foreground">{formatCurrency(reviewInvoice.amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Risk Score</span>
                  <span className="font-bold text-destructive">{reviewInvoice.risk_score}%</span>
                </div>
                {reviewInvoice.flag && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">Flag Reason</div>
                    <div className="text-sm text-destructive font-medium">{reviewInvoice.flag}</div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => reviewInvoice && resolveDuplicate(reviewInvoice.id, 'approve')}>
              <Check className="h-4 w-4 mr-2" />
              Approve
            </Button>
            <Button variant="destructive" className="w-full sm:w-auto" onClick={() => reviewInvoice && resolveDuplicate(reviewInvoice.id, 'block')}>
              <Ban className="h-4 w-4 mr-2" />
              Block
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteInvoice} onOpenChange={() => setDeleteInvoice(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this invoice from {deleteInvoice?.vendor}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.length} Invoices</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.length} selected invoices? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}